import React, { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import "katex/dist/katex.min.css";
import Latex from "react-latex-next";
import { GraduationCap, Activity, BookOpen } from "lucide-react";
import { AIOutput, Step, prepareVideoData } from "@/lib/gemini";
import { Avatar2D } from "./Avatar2D";
import { formatLatexString, cleanCaptionText, cleanForSpeech } from "@/lib/latexUtils";
import { normalizeBoardContentLines } from "@/lib/layoutEngine";
import { isValidRenderableImageUrl } from "@/lib/visualUtils";
import { StructuredSolutionArea } from "./StructuredSolutionArea";

interface RenderVideoPageProps {
  path: string;
}

export function RenderVideoPage({ path }: RenderVideoPageProps) {
  const [id, setId] = useState<string | null>(null);
  const [mode, setMode] = useState<"landscape" | "shorts">("landscape");
  const [lessonData, setLessonData] = useState<AIOutput | null>(null);
  const [stepDurations, setStepDurations] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (url: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  // Time-driven animation clock state managed by Puppeteer
  const [currentTime, setCurrentTime] = useState(0);

  // Parse location search parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const videoId = urlParams.get("id");
    const layoutMode = urlParams.get("mode") === "shorts" ? "shorts" : "landscape";
    
    setId(videoId);
    setMode(layoutMode);
  }, [path]);

  // Fetch saved lesson data
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);

    fetch(`/api/lessons/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Saved lesson not found");
        return res.json();
      })
      .then((data) => {
        const videoData: AIOutput = prepareVideoData(data.videoData);
        setLessonData(videoData);

        // Retrieve precision server timing if merged, otherwise fallback
        if (data.stepDurations && Array.isArray(data.stepDurations)) {
          setStepDurations(data.stepDurations);
        } else {
          const estimated = videoData.steps.map((step) => {
            const rawText = step.audio_explanation || step.explanation || "";
            const text = cleanForSpeech(rawText);
            const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;
            
            const isIndic = /[\u0900-\u097F]/.test(text);
            const isCJK = /[\u4e00-\u9fa5\u3040-\u30ff\uac00-\ud7af]/.test(text);
            
            let wordsPerSecond = 2.25;
            if (isIndic) wordsPerSecond = 1.625;
            if (isCJK) wordsPerSecond = 3.125;
            
            return Math.max(3, wordCount / wordsPerSecond + 1.0);
          });
          setStepDurations(estimated);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Retrieval error:", err);
        const errMsg = err.message || "Failed to load lesson material";
        setError(errMsg);
        (window as any).renderAppletError = errMsg;
        setIsLoading(false);
      });
  }, [id]);

  // Map absolute rendering play clock onto standard steps
  const totalDuration = stepDurations.reduce((a, b) => a + b, 0);

  // Pre-calculate cumulative steps boundary ranges
  const stepBoundaries = React.useMemo(() => {
    const boundaries: { start: number; end: number }[] = [];
    let cumulative = 0;
    for (let i = 0; i < stepDurations.length; i++) {
      boundaries.push({
        start: cumulative,
        end: cumulative + stepDurations[i]
      });
      cumulative += stepDurations[i];
    }
    return boundaries;
  }, [stepDurations]);

  // Resolve active states corresponding to current clock time
  const activeFrameData = React.useMemo(() => {
    if (!lessonData || stepBoundaries.length === 0) return null;
    
    // Clamp layout time limit safely
    const t = Math.max(0, Math.min(currentTime, totalDuration - 0.01));
    
    // Find active step
    let stepIndex = 0;
    for (let i = 0; i < stepBoundaries.length; i++) {
      if (t >= stepBoundaries[i].start && t < stepBoundaries[i].end) {
        stepIndex = i;
        break;
      }
    }

    const stepBoundary = stepBoundaries[stepIndex];
    if (!stepBoundary) return null;

    const activeStep = lessonData.steps[stepIndex];
    const stepDuration = stepDurations[stepIndex] || 5.0;
    const stepProgressSeconds = t - stepBoundary.start;
    const stepProgressRatio = stepProgressSeconds / stepDuration;

    // Word text timing highlights parser
    const rawExplanation = activeStep?.audio_explanation || activeStep?.explanation || "";
    const fullExplanation = cleanForSpeech(rawExplanation);
    const totalChars = fullExplanation.length;
    const currentWordCharIndex = Math.floor(stepProgressRatio * totalChars);

    const isSpeaking = stepProgressSeconds < stepDuration - 0.5;

    // Showcase diagrams rules:
    // 1. Step possesses a dedicated svg diagram or valid image payload
    // 2. Or static upload visual on overview step
    const stepImg = (activeStep as any)?.image_url || (activeStep as any)?.static_image_url;
    const hasValidStepImg = stepImg && isValidRenderableImageUrl(stepImg) && !failedImages.has(stepImg);
    const hasValidGlobalImg = lessonData.static_image_url && isValidRenderableImageUrl(lessonData.static_image_url) && !failedImages.has(lessonData.static_image_url);
    const hasValidSvg = !!activeStep?.diagram_svg && activeStep.diagram_svg.toLowerCase().includes('<svg');

    const isVisualShowcase = (
      hasValidSvg || 
      Boolean(hasValidStepImg) ||
      (Boolean(hasValidGlobalImg) && stepIndex === 1 && !activeStep?.diagram_svg)
    );

    // Resolve latest past available visual image/svg
    let persistentVisual: { type: "svg" | "image"; value: string; description: string } | null = null;
    for (let i = stepIndex; i >= 0; i--) {
      const step = lessonData.steps[i];
      if (step?.diagram_svg && step.diagram_svg.toLowerCase().includes('<svg')) {
        persistentVisual = {
          type: "svg" as const,
          value: step.diagram_svg,
          description: step.diagram_description || step.reference_text || lessonData.diagram_description || ""
        };
        break;
      }
      const pastImg = (step as any)?.image_url || (step as any)?.static_image_url;
      if (pastImg && isValidRenderableImageUrl(pastImg) && !failedImages.has(pastImg)) {
        persistentVisual = {
          type: "image" as const,
          value: pastImg,
          description: step.diagram_description || step.reference_text || lessonData.diagram_description || ""
        };
        break;
      }
    }
    if (!persistentVisual && lessonData.static_image_url && isValidRenderableImageUrl(lessonData.static_image_url) && !failedImages.has(lessonData.static_image_url) && stepIndex >= 0) {
      persistentVisual = {
        type: "image" as const,
        value: lessonData.static_image_url,
        description: lessonData.diagram_description || ""
      };
    }

    return {
      stepIndex,
      activeStep,
      currentWordCharIndex,
      isSpeaking,
      isVisualShowcase,
      persistentVisual
    };
  }, [lessonData, currentTime, stepBoundaries, stepDurations, totalDuration]);

  // Hook Puppeteer control methods to global environment
  useEffect(() => {
    if (isLoading || !lessonData) return;

    (window as any).setAnimationFrame = (t: number) => {
      setCurrentTime(t);
      
      const video = (window as any)._avatarVideo as HTMLVideoElement | undefined;
      if (video) {
        if (!video.paused) {
          video.pause();
        }
        const duration = (video.duration && !isNaN(video.duration) && video.duration > 0) ? video.duration : 10.0;
        const targetTime = t % duration;
        
        if (Math.abs(video.currentTime - targetTime) > 0.001) {
          return new Promise<void>((resolve) => {
            let resolved = false;
            const done = () => {
              if (!resolved) {
                resolved = true;
                video.removeEventListener("seeked", done);
                video.removeEventListener("error", done);
                resolve();
              }
            };
            
            video.addEventListener("seeked", done, { once: true });
            video.addEventListener("error", done, { once: true });
            // Safe 80ms fallback timeout to prevent hangs
            setTimeout(done, 80);
            
            video.currentTime = targetTime;
          });
        }
      }
    };

    (window as any).getTotalDuration = () => {
      const total = stepDurations.reduce((a, b) => a + b, 0);
      return total;
    };

    // Tell Puppeteer that static styling resources have concluded loading
    setTimeout(() => {
      (window as any).renderAppletReady = true;
      console.log("[RenderVideoPage] Puppeteer hooks installed and status ARMED!");
    }, 1500);
  }, [isLoading, lessonData, stepDurations]);

  if (isLoading) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-[#050510] text-slate-300">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4" />
        <span className="font-bold tracking-widest text-sm text-indigo-400">LOADING SCENIC ASSETS...</span>
      </div>
    );
  }

  if (error || !lessonData || !activeFrameData) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-[#050510] text-red-400 p-8 text-center">
        <h2 className="text-2xl font-black mb-4">ENGINE COMPILE ERROR</h2>
        <p className="max-w-lg mb-8 text-slate-400 text-sm">{error || "Failed to parse lesson content metadata"}</p>
      </div>
    );
  }

  const { stepIndex, activeStep, currentWordCharIndex, isSpeaking, isVisualShowcase, persistentVisual } = activeFrameData;

  const formatLatex = (content: string, inline: boolean = true) => {
    if (!content) return "";
    return formatLatexString(content, inline);
  };

  // Helper to slice caption text dynamically for rendering with zero gaps and word highlighting
  const renderSpeechCaptionText = () => {
    const rawText = activeStep?.audio_explanation || activeStep?.explanation || "...";
    const lessonLang = (lessonData as any)?.language || (lessonData as any)?.languageName || "English";
    const fullText = cleanCaptionText(rawText, lessonLang);
    if (!fullText) return <span>...</span>;

    const maxChars = 75;
    const words = fullText.split(/(\s+)/);
    const chunks: { text: string; start: number; end: number }[] = [];
    
    let currentWords: string[] = [];
    let currentLen = 0;
    let runningPos = 0;
    let chunkStart = 0;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const isPunctuationEnd = /[.!?।॥؟。]\s*$/.test(word);

      if (currentLen + word.length > maxChars && currentWords.length > 0) {
        const chunkText = currentWords.join('');
        const chunkEnd = runningPos;
        chunks.push({ text: chunkText, start: chunkStart, end: chunkEnd });
        currentWords = [];
        currentLen = 0;
        chunkStart = chunkEnd;
      }

      currentWords.push(word);
      currentLen += word.length;
      runningPos += word.length;

      if (isPunctuationEnd && currentLen >= 35 && i < words.length - 1) {
        const chunkText = currentWords.join('');
        const chunkEnd = runningPos;
        chunks.push({ text: chunkText, start: chunkStart, end: chunkEnd });
        currentWords = [];
        currentLen = 0;
        chunkStart = chunkEnd;
      }
    }

    if (currentWords.length > 0) {
      const chunkText = currentWords.join('');
      chunks.push({ text: chunkText, start: chunkStart, end: runningPos });
    }

    if (chunks.length === 0) {
      return <span>{fullText}</span>;
    }

    const activeChunk = chunks.find(c => currentWordCharIndex >= c.start && currentWordCharIndex < c.end) || chunks[chunks.length - 1];

    const tokens = activeChunk.text.split(/(\s+)/);
    let tokenPos = activeChunk.start;

    return (
      <span className="inline-flex flex-wrap gap-x-1.5 justify-center items-center">
        {tokens.map((token, idx) => {
          const isSpace = /^\s+$/.test(token);
          const tokenStart = tokenPos;
          tokenPos += token.length;
          const tokenEnd = tokenPos;

          if (isSpace) return <span key={idx}>{token}</span>;

          const isCurrentWord = currentWordCharIndex >= tokenStart && currentWordCharIndex < tokenEnd;

          return (
            <span
              key={idx}
              className={isCurrentWord ? "text-amber-400 font-extrabold scale-105 transition-transform inline-block" : "text-white opacity-95"}
            >
              {token}
            </span>
          );
        })}
      </span>
    );
  };

  const boardContent = activeStep?.board_content || "";
  const normalizedLines = normalizeBoardContentLines(boardContent);
  const boardLines = normalizedLines.length > 0 ? normalizedLines : (boardContent ? [boardContent] : []);

  // Strict safe font size and line gap computation for guaranteed overflow-free presentation
  let fontSizeClass = "text-[2.0rem]";
  let lineGapClass = "gap-6";

  if (boardLines.length === 3) {
    fontSizeClass = "text-[1.7rem]";
    lineGapClass = "gap-5";
  } else if (boardLines.length >= 4) {
    fontSizeClass = "text-[1.45rem]";
    lineGapClass = "gap-3.5";
  }

  const maxLineLength = Math.max(...boardLines.map((l) => l.length), 0);
  if (maxLineLength > 85 && boardLines.length <= 2) {
    fontSizeClass = "text-[1.65rem]";
  } else if (maxLineLength > 85 && boardLines.length >= 3) {
    fontSizeClass = "text-[1.35rem]";
  }

  // LANDSCAPE EXPORT CANVAS (`1920x1080`)
  if (mode === "landscape") {
    return (
      <div 
        id="renderer-canvas-landscape"
        className="w-[1920px] h-[1080px] bg-[#050510] text-[#e2e8f0] font-sans relative flex flex-col gap-8 p-14 overflow-hidden select-none"
        style={{ width: "1920px", height: "1080px" }}
      >
        {/* Top Centered Brand Watermark */}
        <div className="absolute top-5 left-1/2 -translate-x-1/2 z-50 text-center pointer-events-none">
          <span className="font-extrabold text-[22px] tracking-[0.45em] uppercase" style={{ color: "#f5a623" }}>
            professorxai.info
          </span>
        </div>

        {/* Star backdrop decoration */}
        <div 
          className="absolute inset-0 bg-cover bg-center pointer-events-none opacity-80"
          style={{ backgroundImage: "url('/background.png?v=2')" }}
        />
        <div className="absolute inset-0 bg-black/50 pointer-events-none" />

        {/* Top Section: Question Bar */}
        <div className="flex-shrink-0 h-[140px] relative z-10">
          <div className="bg-[#0b0c20]/90 border border-indigo-900/60 p-8 rounded-[36px] shadow-2xl flex items-center justify-between gap-8 h-full">
            <div className="flex items-center gap-6 overflow-hidden flex-1">
              <div className="bg-indigo-500/20 p-4 rounded-2xl flex-shrink-0 shadow-inner">
                <GraduationCap className="w-12 h-12 text-indigo-400" />
              </div>
              <div className="flex flex-col gap-1 overflow-hidden flex-1 min-w-0">
                <span className="font-extrabold text-xs tracking-[0.25em] uppercase text-indigo-400/80">
                  {lessonData.is_doubt_mode ? "DOUBT RESOLUTION" : "QUESTION EXPLANATION"}
                </span>
                <span className="font-bold text-2xl leading-snug text-white line-clamp-2">
                  <Latex strict={false}>{lessonData.question}</Latex>
                </span>
              </div>
            </div>
            {/* Subject Indicator Badge */}
            <div className="px-5 py-2.5 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 font-bold text-sm tracking-wide flex-shrink-0">
              {lessonData.subject}
            </div>
          </div>
        </div>

        {/* Middle Body section */}
        <div className="flex flex-row gap-8 items-stretch flex-1 min-h-0 relative z-10">
          
          {/* Sidebar Area: Professor Avatar + Space stats */}
          <div className="w-[380px] flex flex-col gap-4 flex-shrink-0">
            {/* Subject Topic card */}
            <div className="bg-[#0e1628]/90 rounded-[21px] border border-emerald-800/40 relative overflow-hidden flex flex-col justify-center px-6 h-[72px]">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-lg bg-emerald-500" />
              <span className="text-[9px] font-black uppercase text-emerald-400 tracking-widest mb-0.5">Subject Topic</span>
              <span className="font-bold text-base text-emerald-100 truncate">{lessonData.topic}</span>
            </div>

            {/* Speaking Professor Card */}
            <div className="flex-1 bg-slate-900/40 border border-slate-700/30 rounded-[44px] overflow-hidden p-6 flex flex-col items-center justify-center">
              <div className="w-full h-[470px] rounded-[32px] overflow-hidden border border-slate-800 shadow-inner relative flex items-center justify-center">
                <Avatar2D isSpeaking={isSpeaking} currentTime={currentTime} videoSrc="/xai.mp4" />
              </div>
              <div className="mt-5 font-black text-xs uppercase tracking-[0.3em] text-center text-slate-400/80">
                Professor X
              </div>
            </div>
          </div>

          {/* Core Whiteboard + Visual representation Panel */}
          <div className="flex-1 flex flex-row gap-8 min-h-0">
            
            {/* Left Box: Mathematical Solution steps */}
            <div className="flex-1 flex flex-col gap-8 min-h-0">
              <div className="flex-1 bg-[#020205] rounded-[52px] border-[14px] border-slate-800 relative overflow-hidden flex flex-col shadow-inner">
                {/* Board grid dots overlay */}
                <div 
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{ 
                    backgroundImage: "radial-gradient(#475569 2px, transparent 2px)", 
                    backgroundSize: "32px 32px" 
                  }}
                />
                
                {/* Math values content */}
                <div className="relative z-10 flex-grow p-12 flex flex-col justify-center items-start w-full overflow-hidden">
                  {isVisualShowcase ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8">
                      <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
                        {activeStep?.diagram_svg ? (
                          <div 
                            className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-full [&>svg]:overflow-visible text-indigo-400"
                            dangerouslySetInnerHTML={{ __html: activeStep.diagram_svg }}
                          />
                        ) : ((activeStep as any)?.image_url || (activeStep as any)?.static_image_url) ? (
                          <img 
                            src={(activeStep as any).image_url || (activeStep as any).static_image_url} 
                            alt={activeStep?.diagram_description || "Step Visual"}
                            className="max-w-full max-h-full object-contain rounded-2xl"
                            referrerPolicy="no-referrer"
                            onError={() => {
                              const src = (activeStep as any).image_url || (activeStep as any).static_image_url;
                              if (src) handleImageError(src);
                            }}
                          />
                        ) : lessonData.static_image_url ? (
                          <img 
                            src={lessonData.static_image_url} 
                            alt="Explanation focus"
                            className="max-w-full max-h-full object-contain rounded-2xl"
                            referrerPolicy="no-referrer"
                            onError={() => {
                              if (lessonData.static_image_url) handleImageError(lessonData.static_image_url);
                            }}
                          />
                        ) : null}
                      </div>
                      <div className="mt-8 border-l-4 border-indigo-500 pl-6 w-full max-w-2xl text-left">
                        <p className="text-indigo-200 font-bold italic text-base leading-tight">
                          {activeStep?.diagram_description || lessonData.diagram_description || "Visualizing critical equations step..."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <StructuredSolutionArea
                      step={activeStep}
                      fontSizeClass={fontSizeClass}
                      lineGapClass={lineGapClass}
                      isExport={true}
                      stepIndex={stepIndex}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right Box: Floating diagram representation */}
            {(lessonData.diagram_needed || lessonData.static_image_url) && (
              <div className="w-[370px] bg-slate-900/85 rounded-[44px] border border-slate-700/30 p-7 flex flex-col gap-6 flex-shrink-0 min-h-0 overflow-hidden relative">
                <div className="flex items-center gap-3 px-2 flex-shrink-0">
                  <Activity className="w-5 h-5 text-indigo-400" />
                  <span className="text-slate-400 text-xs font-black uppercase tracking-[0.2em]">Visual Representation</span>
                </div>

                <div className="flex-1 bg-black/60 rounded-[28px] border border-slate-800 flex items-center justify-center p-6 overflow-hidden min-h-0">
                  {isVisualShowcase ? (
                    <div className="flex flex-col items-center gap-4 opacity-30 text-center">
                      <span className="text-xs font-black uppercase tracking-widest text-indigo-400">In Focus on Main Board</span>
                    </div>
                  ) : persistentVisual?.type === "image" ? (
                    <img 
                      src={persistentVisual.value} 
                      alt="Lesson plot"
                      className="w-full h-full object-contain"
                      referrerPolicy="no-referrer"
                      onError={() => handleImageError(persistentVisual.value)}
                    />
                  ) : persistentVisual?.type === "svg" ? (
                    <div 
                      className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:max-h-full [&>svg]:h-auto [&>svg]:overflow-visible"
                      dangerouslySetInnerHTML={{ __html: persistentVisual.value }}
                    />
                  ) : (
                    <div className="text-slate-600 text-xs text-center font-bold tracking-widest uppercase">Awaiting Visual Map</div>
                  )}
                </div>

                <div className="bg-[#050510]/30 border border-slate-800/40 rounded-2xl p-4 flex-shrink-0 h-[92px] overflow-y-hidden flex items-center">
                  <p className="text-slate-300 text-sm leading-snug italic text-center w-full">
                    {persistentVisual?.description || "Concept model visual output."}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Caption Subtitle section */}
        <div className="w-full h-[110px] bg-rose-950/45 border border-rose-900/30 rounded-[30px] p-6 flex items-center justify-center flex-shrink-0 relative z-10 overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-rose-500" />
          <p className="text-white text-2xl font-bold leading-tight tracking-normal text-center max-w-[1500px]">
            {renderSpeechCaptionText()}
          </p>
        </div>
      </div>
    );
  }

  // YOUTUBE SHORTS PORTRAIT CANVAS (`1080x1920`)
  // Rearranges components strictly vertical as specified by the user's layout plan and reference image
  return (
    <div 
      id="renderer-canvas-shorts"
      className="w-[1080px] h-[1920px] bg-[#030308] text-[#e2e8f0] font-sans relative flex flex-col gap-8 p-14 overflow-hidden select-none"
      style={{ width: "1080px", height: "1920px" }}
    >
      {/* Galactic backdrop decoration */}
      <div 
        className="absolute inset-0 bg-cover bg-center pointer-events-none opacity-95 scale-105"
        style={{ backgroundImage: "url('/background.png?v=2')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/20 to-black/75 pointer-events-none" />

      {/* 1. TOP SECTION: Question bar */}
      <div className="flex flex-col gap-6 relative z-10 flex-shrink-0 pt-8 animate-fade-in">
        
        {/* Large Cinematic Question card card */}
        <div className="bg-[#0b0c20]/90 border border-indigo-900/60 p-8 rounded-[36px] shadow-2xl flex items-center gap-8 h-[160px] w-full">
          <div className="flex items-center gap-6 overflow-hidden flex-1">
            <div className="bg-indigo-500/20 p-4 rounded-2xl flex-shrink-0 shadow-inner">
              <GraduationCap className="w-12 h-12 text-indigo-400" />
            </div>
            <div className="flex flex-col gap-1.5 overflow-hidden flex-1">
              <span className="font-extrabold text-xs tracking-[0.25em] uppercase text-indigo-400/80">
                {lessonData.is_doubt_mode ? "AI DOUBT SHORTS" : "professor x lesson"}
              </span>
              <span className="font-bold text-[22px] leading-snug text-white line-clamp-2 max-w-[850px]">
                <Latex strict={false}>{lessonData.question}</Latex>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. CENTER SECTION: Dominant Whiteboard formula board */}
      <div className="relative z-10 w-full flex-shrink-0">
        <div className="w-full bg-[#020205] rounded-[52px] border-[14px] border-slate-800 shadow-2xl h-[570px] relative overflow-hidden flex flex-col">
          {/* Board grid dots overlay */}
          <div 
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ 
              backgroundImage: "radial-gradient(#475569 2px, transparent 2px)", 
              backgroundSize: "32px 32px" 
            }}
          />

          <div className="relative z-10 flex-grow p-12 flex flex-col justify-center items-start w-full overflow-hidden">
            {isVisualShowcase ? (
              <div className="w-full h-full flex flex-col items-center justify-center p-8">
                <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
                  {activeStep?.diagram_svg ? (
                    <div 
                      className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-full [&>svg]:overflow-visible text-indigo-400"
                      dangerouslySetInnerHTML={{ __html: activeStep.diagram_svg }}
                    />
                  ) : ((activeStep as any)?.image_url || (activeStep as any)?.static_image_url) ? (
                    <img 
                      src={(activeStep as any).image_url || (activeStep as any).static_image_url} 
                      alt={activeStep?.diagram_description || "Step Visual"}
                      className="max-w-full max-h-full object-contain rounded-2xl"
                      referrerPolicy="no-referrer"
                      onError={() => {
                        const src = (activeStep as any).image_url || (activeStep as any).static_image_url;
                        if (src) handleImageError(src);
                      }}
                    />
                  ) : lessonData.static_image_url ? (
                    <img 
                      src={lessonData.static_image_url} 
                      alt="Explanation focus"
                      className="max-w-full max-h-full object-contain rounded-2xl"
                      referrerPolicy="no-referrer"
                      onError={() => {
                        if (lessonData.static_image_url) handleImageError(lessonData.static_image_url);
                      }}
                    />
                  ) : null}
                </div>
                <div className="mt-8 border-l-4 border-indigo-500 pl-6 w-full text-left">
                  <p className="text-indigo-200 font-bold italic text-base leading-tight">
                    {activeStep?.diagram_description || lessonData.diagram_description || "Visualizing critical equations step..."}
                  </p>
                </div>
              </div>
            ) : (
              <StructuredSolutionArea
                step={activeStep}
                fontSizeClass={fontSizeClass}
                lineGapClass={lineGapClass}
                isExport={true}
                stepIndex={stepIndex}
              />
            )}
          </div>
        </div>
      </div>

      {/* 3. CAPTIONS SECTION: Subtitle strip */}
      <div className="w-full h-[110px] bg-rose-950/45 border border-rose-900/30 rounded-[30px] p-6 flex items-center justify-center flex-shrink-0 relative z-10 overflow-hidden shadow-2xl">
        <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-rose-500" />
        <p className="text-white text-2xl font-bold leading-tight tracking-normal text-center max-w-[900px] px-2">
          {renderSpeechCaptionText()}
        </p>
      </div>

      {/* Brand Watermark (Shifted below captions list in shorts layout) */}
      <div className="w-full flex justify-center z-50 text-center pointer-events-none mt-1 -mb-3">
        <span className="font-extrabold text-[24px] tracking-[0.45em] uppercase" style={{ color: "#f5a623" }}>
          professorxai.info
        </span>
      </div>

      {/* 4. BOTTOM SECTION: Aligned row elements for Professor Avatar & Visual Representation panel */}
      <div className="flex flex-row gap-8 relative z-10 h-[760px] flex-shrink-0 items-stretch">
        
        {/* Left Aligned: Professor avatar panel, takes ~36% space */}
        <div className="w-[350px] bg-slate-900/40 border border-slate-700/30 rounded-[44px] p-6 flex flex-col items-center justify-center shadow-xl flex-shrink-0">
          <div className="w-full flex-grow rounded-[32px] overflow-hidden border border-slate-800 shadow-inner relative flex-shrink-0">
            <Avatar2D isSpeaking={isSpeaking} currentTime={currentTime} videoSrc="/xai.mp4" />
          </div>
          <div className="mt-5 font-black text-[21px] uppercase tracking-[0.32em] text-center text-amber-500 filter drop-shadow">
            Professor X
          </div>
        </div>

        {/* Right Aligned: Visual representation graph panel, takes remaining space */}
        <div className="flex-1 bg-slate-900/85 rounded-[44px] border border-slate-700/30 p-7 flex flex-col gap-6 shadow-xl overflow-hidden justify-between relative">
          
          {/* Shifted Topic or Subject Badge in its own dedicated premium horizontal section at top of panel */}
          <div className="w-full py-4.5 px-6 rounded-2xl bg-emerald-950/75 border border-emerald-800/40 text-emerald-300 font-extrabold text-[15px] tracking-[0.2em] uppercase shadow-md text-center flex-shrink-0">
            {lessonData.subject} {" > "} {lessonData.topic}
          </div>

          {/* Header strip */}
          <div className="flex items-center gap-3 px-2 flex-shrink-0">
            <Activity className="w-5 h-5 text-indigo-400" />
            <span className="text-slate-400 text-xs font-black uppercase tracking-[0.25em]">Visual Representation</span>
          </div>

          {/* Graphic viewport */}
          <div className="flex-1 bg-black/60 rounded-[28px] border border-slate-800 flex items-center justify-center p-6 overflow-hidden min-h-0">
            {isVisualShowcase ? (
              <div className="flex flex-col items-center gap-4 opacity-30 text-center">
                <span className="text-xs font-black uppercase tracking-widest text-[#5c6883]">In Focus on Main Board</span>
              </div>
            ) : persistentVisual?.type === "image" ? (
              <img 
                src={persistentVisual.value} 
                alt="Graph visual"
                className="w-full h-full object-contain"
                referrerPolicy="no-referrer"
              />
            ) : persistentVisual?.type === "svg" ? (
              <div 
                className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:max-h-full [&>svg]:h-auto [&>svg]:overflow-visible"
                dangerouslySetInnerHTML={{ __html: persistentVisual.value }}
              />
            ) : (
              <div className="text-slate-600 text-xs text-center font-bold tracking-widest uppercase">Awaiting Visual Map</div>
            )}
          </div>

          {/* Brief dynamic description */}
          <div className="bg-[#050510]/30 border border-slate-800/40 rounded-2xl p-4 flex-shrink-0 h-[92px] overflow-y-hidden flex items-center">
            <p className="text-slate-300 text-sm leading-snug italic text-center w-full">
               {persistentVisual?.description || "Concept model visual output."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
